import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatEuro, formatDate, daysUntil } from "../lib/format";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Button } from "../components/ui/button";
import { Plus, Wallet, TrendingDown, Clock, Euro, CalendarClock, Coins, Scale, AlertCircle, LineChart as LineChartIcon } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import DebtForm from "../components/DebtForm";
import PaymentDialog from "../components/PaymentDialog";
import StatusBadge from "../components/StatusBadge";

const MONTHLY_RANGES = [
  { value: "3", label: "Last 3 months" },
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
];

const formatMonthShort = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [networth, setNetworth] = useState(null);
  const [monthlyRange, setMonthlyRange] = useState("3");
  const [monthly, setMonthly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, n] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/networth/summary"),
      ]);
      setSummary(s.data);
      setNetworth(n.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const loadMonthly = async () => {
      try {
        const { data } = await api.get("/cashflow", { params: { months: monthlyRange } });
        setMonthly(data);
      } catch (err) {
        console.warn("[Dashboard] cashflow load failed:", err.message);
        setMonthly(null);
      }
    };
    loadMonthly();
  }, [monthlyRange]);

  if (loading && !summary) {
    return (
      <div className="text-[#8B9389] tracking-[0.2em] uppercase text-xs">Loading dashboard…</div>
    );
  }

  const t = summary?.totals || {};
  const isEmpty = (t.debt_count || 0) === 0;

  return (
    <div className="space-y-10 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="label-kicker">Overview</p>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
            Your balance sheet
          </h1>
          <p className="text-[#5C635A] mt-2 max-w-xl">
            A calm view of what you owe, what you have paid, and when the last installment is due.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white h-11 px-5 self-start sm:self-auto transition-all hover:-translate-y-0.5"
          data-testid="dashboard-add-debt"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add debt
        </Button>
      </div>

      {networth && (networth.asset_count > 0 || networth.debt_count > 0) && (
        <NetWorthBanner networth={networth} />
      )}

      {!isEmpty && ((t.stale_debts || 0) + (t.stale_assets || 0)) > 0 && (
        <StaleRefreshCallout staleDebts={t.stale_debts || 0} staleAssets={t.stale_assets || 0} />
      )}

      {isEmpty ? (
        <EmptyState onAdd={() => setAddOpen(true)} />
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
            <StatCard
              icon={<Wallet className="w-4 h-4" />}
              kicker="Debt remaining"
              value={formatEuro(t.debt_left)}
              sub={`${t.debt_count} active debts`}
              accent="#2C4A3B"
              testId="stat-debt-left"
            />
            <StatCard
              icon={<Euro className="w-4 h-4" />}
              kicker="Monthly outflow"
              value={formatEuro(t.monthly_scheduled)}
              sub="your monthly commitment"
              accent="#E07A5F"
              testId="stat-monthly"
            />
            <StatCard
              icon={<TrendingDown className="w-4 h-4" />}
              kicker="Total paid so far"
              value={formatEuro(t.total_paid)}
              sub={`of ${formatEuro((t.initial_debt || 0) + (t.extra_charges || 0) + (t.interest_added || 0))} owed`}
              accent="#829A88"
              testId="stat-paid"
            />
            <StatCard
              icon={<Clock className="w-4 h-4" />}
              kicker="Interest added"
              value={formatEuro(t.interest_added)}
              sub={`${formatEuro(t.extra_charges)} extra charges`}
              accent="#C89B6A"
              testId="stat-interest"
            />
          </section>

          <section className="card-soft p-6" data-testid="monthly-summary-widget">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div>
                <p className="label-kicker">Monthly summary</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
                  <LineChartIcon className="w-5 h-5 text-[#2C4A3B]" strokeWidth={1.7} />
                  What flowed in and out
                </h3>
              </div>
              <Select value={monthlyRange} onValueChange={setMonthlyRange}>
                <SelectTrigger className="w-40 rounded-xl border-[#E5E3DB] bg-white" data-testid="monthly-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHLY_RANGES.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`monthly-range-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {monthly && monthly.months && monthly.months.length > 0 ? (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="monthly-summary-table">
                  <thead>
                    <tr className="text-left label-kicker border-b border-[#EEEBE2]">
                      <th className="py-3 px-2">Month</th>
                      <th className="py-3 px-2 text-right">Paid</th>
                      <th className="py-3 px-2 text-right">Interest</th>
                      <th className="py-3 px-2 text-right">Extra charges</th>
                      <th className="py-3 px-2 text-right">Net change</th>
                      <th className="py-3 px-2 text-right">Total debt · end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.months.map((m) => (
                      <tr key={m.month} className="border-b border-[#EEEBE2] hover:bg-[#FAF8F2]/60" data-testid="monthly-row">
                        <td className="py-3 px-2 text-[#1D3328] font-medium">{formatMonthShort(m.month)}</td>
                        <td className="py-3 px-2 text-right num text-[#2C4A3B]">
                          {m.payments_out > 0 ? `−${formatEuro(m.payments_out)}` : formatEuro(0)}
                        </td>
                        <td className="py-3 px-2 text-right num text-[#C89B6A]">
                          {m.interest_added > 0 ? `+${formatEuro(m.interest_added)}` : formatEuro(0)}
                        </td>
                        <td className="py-3 px-2 text-right num text-[#B04A32]">
                          {m.extra_charges_added > 0 ? `+${formatEuro(m.extra_charges_added)}` : formatEuro(0)}
                        </td>
                        <td
                          className="py-3 px-2 text-right num font-medium"
                          style={{ color: m.net_debt_change <= 0 ? "#2C4A3B" : "#B04A32" }}
                        >
                          {m.net_debt_change >= 0 ? "+" : ""}{formatEuro(m.net_debt_change)}
                        </td>
                        <td className="py-3 px-2 text-right num font-medium text-[#E07A5F]">
                          {formatEuro(m.total_debt)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-[#FAF8F2]" data-testid="monthly-totals-row">
                      <td className="py-3 px-2 label-kicker">Totals</td>
                      <td className="py-3 px-2 text-right num text-[#2C4A3B] font-medium">
                        −{formatEuro(monthly.totals.payments_out)}
                      </td>
                      <td className="py-3 px-2 text-right num text-[#C89B6A] font-medium">
                        +{formatEuro(monthly.totals.interest_added)}
                      </td>
                      <td className="py-3 px-2 text-right num text-[#B04A32] font-medium">
                        +{formatEuro(monthly.totals.extra_charges_added)}
                      </td>
                      <td
                        className="py-3 px-2 text-right num font-semibold"
                        style={{ color: monthly.totals.net_debt_change <= 0 ? "#2C4A3B" : "#B04A32" }}
                      >
                        {monthly.totals.net_debt_change >= 0 ? "+" : ""}{formatEuro(monthly.totals.net_debt_change)}
                      </td>
                      <td className="py-3 px-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[#8B9389] text-sm">No activity in this period.</p>
            )}
          </section>

          <section className="card-soft p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="label-kicker">By creditor</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1">Where the money is owed</h3>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(summary.by_creditor || []).slice(0, 8)} margin={{ left: 0, right: 12, top: 8, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEEBE2" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5C635A" }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#5C635A" }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip
                    formatter={(v) => formatEuro(v)}
                    contentStyle={{ background: "#FFF", border: "1px solid #E5E3DB", borderRadius: 12 }}
                  />
                  <Bar dataKey="amount" fill="#2C4A3B" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="card-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="label-kicker">Upcoming</p>
                <h3 className="font-display text-xl text-[#1D3328] mt-1 flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-[#E07A5F]" strokeWidth={1.7} />
                  Upcoming payments
                </h3>
              </div>
            </div>
            {(summary.upcoming_payments || []).length === 0 ? (
              <p className="text-[#8B9389] text-sm">No upcoming payments scheduled.</p>
            ) : (
              <ul className="divide-y divide-[#EEEBE2]">
                {summary.upcoming_payments.map((u) => {
                  const d = daysUntil(u.next_payment_date);
                  const urgent = d !== null && d <= 7;
                  return (
                    <li key={u.debt_id} className="py-3 flex items-center justify-between gap-3">
                      <Link to={`/debts/${u.debt_id}`} className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-xs font-mono tracking-tight text-[#8B9389]">{u.code}</span>
                          <span className="font-medium text-[#1D3328] truncate">{u.owed_to}</span>
                          {urgent && d < 0 && (
                            <StatusBadge status="not_serviced" />
                          )}
                          <span className="text-xs text-[#8B9389] hidden sm:inline">· {u.who}</span>
                        </div>
                      </Link>
                      <div className="text-right">
                        <div className="text-sm font-medium num text-[#1D3328]">{formatEuro(u.amount)}</div>
                        <div className={`text-xs ${urgent ? "text-[#E07A5F]" : "text-[#8B9389]"}`}>
                          {formatDate(u.next_payment_date)}
                          {d !== null && ` · ${d >= 0 ? `in ${d}d` : `${-d}d ago`}`}
                        </div>
                      </div>
                      <button
                        onClick={() => setPayTarget({
                          id: u.debt_id, code: u.code, owed_to: u.owed_to,
                          monthly_installment: u.amount, debt_left: null,
                        })}
                        className="ml-2 inline-flex items-center gap-1 rounded-lg bg-[#EEEBE2] hover:bg-[#E5DCC5] text-[#1D3328] text-xs font-medium px-2.5 py-1.5 transition-colors"
                        data-testid="upcoming-quick-pay"
                        title="Record payment"
                      >
                        <Coins className="w-3.5 h-3.5" /> Pay
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}

      <DebtForm open={addOpen} onOpenChange={setAddOpen} onSaved={() => load()} />
      <PaymentDialog
        open={Boolean(payTarget)}
        onOpenChange={(v) => { if (!v) setPayTarget(null); }}
        debt={payTarget}
        onSaved={() => load()}
      />
    </div>
  );
}

function StatCard({ icon, kicker, value, sub, accent, testId }) {
  return (
    <div className="card-soft p-6 transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="label-kicker">{kicker}</p>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}18`, color: accent }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-3xl font-medium num text-[#1A1C1A] mt-3">{value}</div>
      <div className="text-xs text-[#8B9389] mt-1">{sub}</div>
    </div>
  );
}

function StaleRefreshCallout({ staleDebts, staleAssets }) {
  const total = staleDebts + staleAssets;
  const parts = [];
  if (staleDebts > 0) parts.push(`${staleDebts} debt${staleDebts === 1 ? "" : "s"}`);
  if (staleAssets > 0) parts.push(`${staleAssets} asset${staleAssets === 1 ? "" : "s"}`);
  return (
    <section
      className="rounded-2xl border border-[#E07A5F]/40 bg-[#FAEEE5] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
      data-testid="stale-refresh-callout"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-[#E07A5F]/15 text-[#B04A32] flex items-center justify-center shrink-0">
          <AlertCircle className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <p className="label-kicker text-[#B04A32]">Monthly refresh due</p>
          <p className="text-sm text-[#5C2A1E] mt-0.5">
            <span className="font-medium text-[#B04A32]">{parts.join(" and ")}</span> have not been updated in over 30 days.
            Touch them so your totals stay accurate.
          </p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {staleDebts > 0 && (
          <Link
            to="/debts"
            className="inline-flex items-center gap-1.5 rounded-xl bg-white hover:bg-[#FBF1EA] border border-[#E07A5F]/40 text-[#B04A32] text-xs font-medium px-3 py-2 transition-colors"
            data-testid="stale-go-to-debts"
          >
            Review debts →
          </Link>
        )}
        {staleAssets > 0 && (
          <Link
            to="/assets"
            className="inline-flex items-center gap-1.5 rounded-xl bg-white hover:bg-[#FBF1EA] border border-[#E07A5F]/40 text-[#B04A32] text-xs font-medium px-3 py-2 transition-colors"
            data-testid="stale-go-to-assets"
          >
            Review assets →
          </Link>
        )}
      </div>
    </section>
  );
}

function NetWorthBanner({ networth }) {
  if (!networth) return null;
  const positive = networth.net_worth >= 0;
  return (
    <section className="card-soft p-6 sm:p-8 relative overflow-hidden" data-testid="networth-banner">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #2C4A3B 0%, transparent 50%), radial-gradient(circle at 80% 70%, #E07A5F 0%, transparent 50%)",
        }}
      />
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[#5C635A]">
              <Scale className="w-4 h-4" strokeWidth={1.7} />
              <p className="label-kicker text-[#5C635A]">Family net worth</p>
            </div>
            <Link
              to="/networth"
              className="text-xs font-medium text-[#2C4A3B] hover:text-[#1D3328] underline underline-offset-4"
              data-testid="dashboard-view-timeline"
            >
              View timeline →
            </Link>
          </div>
          <div
            className="font-display text-5xl sm:text-6xl font-medium num mt-3"
            style={{ color: positive ? "#2C4A3B" : "#B04A32" }}
            data-testid="networth-value"
          >
            {formatEuro(networth.net_worth)}
          </div>
          <p className="text-[#5C635A] text-sm mt-2">
            <span className="num font-medium text-[#1D3328]">{formatEuro(networth.assets_total)}</span> in assets
            {" "}minus{" "}
            <span className="num font-medium text-[#E07A5F]">{formatEuro(networth.debts_total)}</span> in debts
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
          <div className="rounded-xl bg-[#E5EFE7] border border-[#829A88]/30 px-4 py-3">
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#5C635A]">Assets</p>
            <p className="num text-xl font-medium text-[#2C4A3B] mt-0.5">{formatEuro(networth.assets_total)}</p>
            <p className="text-[10px] text-[#8B9389]">{networth.asset_count} item{networth.asset_count !== 1 ? "s" : ""}</p>
          </div>
          <div className="rounded-xl bg-[#FAEEE5] border border-[#E07A5F]/30 px-4 py-3">
            <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#5C635A]">Debts</p>
            <p className="num text-xl font-medium text-[#B04A32] mt-0.5">{formatEuro(networth.debts_total)}</p>
            <p className="text-[10px] text-[#8B9389]">{networth.debt_count} debt{networth.debt_count !== 1 ? "s" : ""}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card-soft p-10 text-center">
      <img
        src="https://images.unsplash.com/photo-1777460978703-32d290b8f9e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwZWFjZWZ1bCUyMG1pbmltYWwlMjBkZXNrJTIwcGxhbnR8ZW58MHx8fHwxNzc4MDgzMDIyfDA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="w-40 h-40 object-cover rounded-2xl mx-auto mb-6 opacity-90"
      />
      <p className="label-kicker">Nothing here yet</p>
      <h3 className="font-display text-2xl text-[#1D3328] mt-2">Add your first debt</h3>
      <p className="text-[#5C635A] mt-2 max-w-md mx-auto">
        Start with the creditor, the initial amount and your monthly installment. You can update
        interest any month.
      </p>
      <Button
        onClick={onAdd}
        className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white mt-6"
        data-testid="empty-add-debt"
      >
        <Plus className="w-4 h-4 mr-1.5" /> Add debt
      </Button>
    </div>
  );
}
